/**
 * Wizard Footer Component
 *
 * Displays navigation buttons (Back, Next, Save Draft, Complete) at the bottom of the wizard.
 * Handles button states based on current step and validation.
 */

import React from 'react';
import { Button } from '../common/Button';
import { useWizard } from './Wizard';

export interface WizardFooterProps {
  className?: string;
  showSaveDraft?: boolean;
  nextLabel?: string;
  backLabel?: string;
  completeLabel?: string;
  saveDraftLabel?: string;
}

export const WizardFooter: React.FC<WizardFooterProps> = ({
  className = '',
  showSaveDraft = true,
  nextLabel = 'Next',
  backLabel = 'Back',
  completeLabel = 'Complete',
  saveDraftLabel = 'Save & Continue Later',
}) => {
  const {
    isFirstStep,
    isLastStep,
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    complete,
    saveDraft,
  } = useWizard();

  return (
    <div className={`wizard-footer flex items-center justify-between gap-4 pt-6 border-t border-gray-200 ${className}`}>
      {/* Left Side - Back Button */}
      <div>
        {!isFirstStep && (
          <Button
            variant="outline"
            onClick={goBack}
            disabled={!canGoBack}
          >
            ← {backLabel}
          </Button>
        )}
      </div>

      {/* Right Side - Save Draft & Next/Complete Buttons */}
      <div className="flex items-center gap-3">
        {showSaveDraft && (
          <Button
            variant="ghost"
            onClick={saveDraft}
          >
            {saveDraftLabel}
          </Button>
        )}

        {!isLastStep ? (
          <Button
            variant="primary"
            onClick={goNext}
            disabled={!canGoNext}
          >
            {nextLabel} →
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={complete}
            disabled={!canGoNext}
          >
            {completeLabel}
          </Button>
        )}
      </div>
    </div>
  );
};
